import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Loader2, Plus, Trash2, DollarSign, EyeOff, Eye } from "lucide-react";
import api from "@/lib/api";

const TIER_ORDER = ["keepsake", "legacy", "pro"];

function TierCard({ tierKey, tier, onChange }) {
  const addFeature = () => onChange({ ...tier, features: [...(tier.features || []), "New feature"] });
  const editFeature = (i, v) => onChange({ ...tier, features: tier.features.map((f, idx) => idx === i ? v : f) });
  const removeFeature = (i) => onChange({ ...tier, features: tier.features.filter((_, idx) => idx !== i) });

  return (
    <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5" data-testid={`pricing-card-${tierKey}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="font-display text-lg font-semibold capitalize">{tierKey}</div>
        <button onClick={() => onChange({ ...tier, enabled: !tier.enabled })} data-testid={`pricing-toggle-${tierKey}`} className={`inline-flex items-center gap-1.5 px-2.5 h-8 rounded-lg text-xs font-semibold border ${tier.enabled ? "border-[hsl(var(--accent))]/40 text-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10" : "border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))]"}`}>
          {tier.enabled ? <><Eye size={12} /> Enabled</> : <><EyeOff size={12} /> Disabled</>}
        </button>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2">
        <div>
          <label className="block text-[11px] font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-1">Label</label>
          <input data-testid={`pricing-label-${tierKey}`} value={tier.label || ""} onChange={(e) => onChange({ ...tier, label: e.target.value })} className="w-full h-9 px-3 rounded-lg border border-[hsl(var(--input))] bg-[hsl(var(--background))] text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]" />
        </div>
        <div>
          <label className="block text-[11px] font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-1">Price display</label>
          <input data-testid={`pricing-price-${tierKey}`} value={tier.price_display || ""} onChange={(e) => onChange({ ...tier, price_display: e.target.value })} className="w-full h-9 px-3 rounded-lg border border-[hsl(var(--input))] bg-[hsl(var(--background))] text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]" />
        </div>
      </div>
      <div className="mt-2">
        <label className="block text-[11px] font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-1">Subtitle</label>
        <input value={tier.subtitle || ""} onChange={(e) => onChange({ ...tier, subtitle: e.target.value })} className="w-full h-9 px-3 rounded-lg border border-[hsl(var(--input))] bg-[hsl(var(--background))] text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]" />
      </div>
      <div className="mt-3">
        <div className="flex items-center justify-between">
          <label className="block text-[11px] font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))]">Features</label>
          <button onClick={addFeature} className="inline-flex items-center gap-1 text-xs font-semibold text-[hsl(var(--accent))] hover:underline"><Plus size={12} /> Add</button>
        </div>
        <ul className="mt-2 space-y-1.5">
          {(tier.features || []).map((f, i) => (
            <li key={i} className="flex items-center gap-2">
              <input value={f} onChange={(e) => editFeature(i, e.target.value)} className="flex-1 h-8 px-2 rounded-md border border-[hsl(var(--input))] bg-[hsl(var(--background))] text-sm focus:outline-none focus:ring-1 focus:ring-[hsl(var(--ring))]" />
              <button onClick={() => removeFeature(i)} className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--destructive))] p-1"><Trash2 size={12} /></button>
            </li>
          ))}
          {(!tier.features || tier.features.length === 0) && <li className="text-xs text-[hsl(var(--muted-foreground))]">No features listed yet.</li>}
        </ul>
      </div>
    </div>
  );
}

export default function AdminPricing() {
  const [pricing, setPricing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get("/admin/settings/pricing"); setPricing(r.data?.pricing); }
    catch { toast.error("Could not load pricing"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/admin/settings/pricing", { pricing });
      setPricing(r.data?.pricing || pricing);
      toast.success("Pricing saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (!pricing) return <div className="p-8"><div className="h-8 w-40 rounded bg-[hsl(var(--muted))] animate-pulse" /></div>;

  return (
    <div className="p-6 sm:p-8 max-w-6xl" data-testid="admin-pricing">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold flex items-center gap-2"><DollarSign size={20} className="text-[hsl(var(--accent))]" /> Pricing & plan copy</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">Edit the labels, headline price, and feature bullets shown on the public pricing surfaces. Actual charge amounts remain controlled by Stripe.</p>
        </div>
        <button data-testid="admin-pricing-save" onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 px-4 h-10 rounded-xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-semibold text-sm disabled:opacity-60">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
        </button>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        {TIER_ORDER.map((k) => (
          <TierCard key={k} tierKey={k} tier={pricing[k] || {}} onChange={(v) => setPricing({ ...pricing, [k]: v })} />
        ))}
      </div>

      <div className="mt-6 rounded-2xl border border-dashed border-[hsl(var(--border))] p-4 text-xs text-[hsl(var(--muted-foreground))]">
        The public read endpoint is <code className="px-1 py-0.5 rounded bg-[hsl(var(--muted))] font-mono">GET /api/public/pricing</code>. Changes take effect immediately for any page using the dynamic pricing feed.
      </div>
    </div>
  );
}
